import { drawDOM, exportPDF } from '@progress/kendo-drawing';
import { saveAs } from '@progress/kendo-file-saver';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { doneLabel, formatDuration, IAgentTraceStep } from './AgentTrace';

export interface IExportMessage {
  role: 'user' | 'agent';
  text: string;
  trace?: IAgentTraceStep[];
}

export interface IExportChat {
  title: string;
  agentName: string;
  messages: IExportMessage[];
}

const fileSafe = (value: string): string => value.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60) || 'chat';

const fileName = (chat: IExportChat, extension: string): string => {
  const stamp = new Date().toISOString().slice(0, 10);
  return `${fileSafe(chat.agentName)} - ${fileSafe(chat.title)} - ${stamp}.${extension}`;
};

const speaker = (chat: IExportChat, message: IExportMessage): string => (message.role === 'user' ? 'You' : chat.agentName);

const exportedOn = (): string => new Date().toLocaleString();

// The steps behind a reply, in a form that reads the same in Markdown and PDF.
const traceDataset = (trace: IAgentTraceStep[]): string | undefined => trace.find((s) => s.datasetName)?.datasetName ?? undefined;

/** Builds the chat as a Markdown document. */
export function buildChatMarkdown(chat: IExportChat): string {
  const lines: string[] = [`# ${chat.title}`, '', `_${chat.agentName} · exported ${exportedOn()}_`, ''];

  chat.messages.forEach((message) => {
    lines.push('---', '', `**${speaker(chat, message)}**`, '', message.text, '');

    if (message.role === 'agent' && message.trace?.length) {
      const dataset = traceDataset(message.trace);
      lines.push('**How this was answered**', '');
      if (dataset) lines.push(`- Dataset: ${dataset}`);
      message.trace.forEach((step, index) => {
        const time = step.durationSeconds != null ? ` (${formatDuration(step.durationSeconds)})` : '';
        lines.push(`${index + 1}. ${doneLabel(step)}${time}${step.thought ? ` - ${step.thought}` : ''}`);
      });
      lines.push('');
      message.trace.forEach((step) =>
        step.dax.forEach((dax) => {
          lines.push('```dax', dax, '```', '');
        }),
      );
    }
  });

  return `${lines.join('\n').trimEnd()}\n`;
}

export function exportChatAsMarkdown(chat: IExportChat): void {
  saveAs(`data:text/markdown;charset=utf-8,${encodeURIComponent(buildChatMarkdown(chat))}`, fileName(chat, 'md'));
}

// The PDF is drawn from a throwaway DOM tree rather than the live chat, so it is always a clean light
// document (no app chrome, no dark theme) and long chats paginate properly. Styles are inline for the
// same reason: this tree lives outside the themed chat view.
//
// Fonts are forced with !important on every element. The PDF renderer embeds any web font the text uses,
// and the app's Inter font (Inter.woff2, declared as "truetype") can't be parsed by it: the export then
// throws "missing table head" and never finishes. Standard PDF fonts need no embedding.
//
// The tree still picks up the app's global element styles (theme-light/theme-dark.scss): p gets line-height: 1
// and h1 is uppercase, and in the dark theme both get light text colours. Those are reset below.
// Layout width in CSS px; see PDF_SCALE.
const PDF_LAYOUT_WIDTH = 680;

const PDF_STYLES = `
  .agents-export-doc, .agents-export-doc * { font-family: Arial, Helvetica, sans-serif !important; box-sizing: border-box; overflow-wrap: anywhere; word-break: break-word; }
  .agents-export-doc pre, .agents-export-doc pre *, .agents-export-doc code { font-family: 'Courier New', Courier, monospace !important; }
  .agents-export-doc { width: ${PDF_LAYOUT_WIDTH}px; padding: 0; background: #fff; color: #121619; font-size: 14px; line-height: 1.5; }
  .agents-export-doc h1 { margin: 0 0 4px; color: inherit; font-size: 24px; line-height: 1.25; text-transform: none; }
  .agents-export-doc .meta { margin-bottom: 20px; color: #6b7280; font-size: 12px; }
  .agents-export-doc .msg { width: 100%; margin: 0 0 16px; padding: 12px 14px; border: 1px solid #e7eaee; border-radius: 8px; }
  .agents-export-doc .msg.user { background: #f4f5f7; }
  .agents-export-doc .who { margin-bottom: 4px; color: #4d5358; font-size: 11px; font-weight: bold; letter-spacing: 0.06em; text-transform: uppercase; }
  .agents-export-doc .body *, .agents-export-doc .body { font-size: 14px !important; }
  .agents-export-doc .body h1, .agents-export-doc .body h2, .agents-export-doc .body h3, .agents-export-doc .body h4 { margin: 10px 0 4px; color: inherit; font-size: 16px !important; font-weight: bold; line-height: 1.3; text-transform: none; }
  .agents-export-doc .body p { margin: 0 0 6px; color: inherit; line-height: 1.5; }
  .agents-export-doc .body ul, .agents-export-doc .body ol { margin: 0 0 6px; padding-left: 20px; }
  .agents-export-doc .body table { width: 100%; margin: 0 0 6px; border-collapse: collapse; table-layout: fixed; }
  .agents-export-doc .body th, .agents-export-doc .body td { padding: 4px 8px; border: 1px solid #d9dee6; text-align: left; }
  .agents-export-doc .body th { background: #f4f5f7; font-size: 12px !important; }
  .agents-export-doc pre { width: 100%; margin: 6px 0 0; padding: 8px 10px; border-radius: 6px; background: #f4f5f7; font-size: 11px !important; white-space: pre-wrap; }
  .agents-export-doc pre * { font-size: 11px !important; white-space: pre-wrap; }
  .agents-export-doc .trace { margin-top: 10px; padding-top: 8px; border-top: 1px dashed #d9dee6; color: #4d5358; font-size: 12px; }
  .agents-export-doc .trace b { color: #121619; }
  .agents-export-doc .trace ol { margin: 2px 0 0; padding-left: 18px; }
`;

const el = (tag: string, className?: string, text?: string): HTMLElement => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

function buildPdfDocument(chat: IExportChat): HTMLElement {
  const root = el('div', 'agents-export-doc');
  const style = document.createElement('style');
  style.textContent = PDF_STYLES;
  root.appendChild(style);
  root.appendChild(el('h1', undefined, chat.title));
  root.appendChild(el('div', 'meta', `${chat.agentName} · exported ${exportedOn()}`));

  chat.messages.forEach((message) => {
    const block = el('div', `msg ${message.role}`);
    block.appendChild(el('div', 'who', speaker(chat, message)));

    const body = el('div', 'body');
    if (message.role === 'agent') {
      body.innerHTML = DOMPurify.sanitize(marked.parse(message.text, { async: false }) as string);
    } else {
      body.textContent = message.text;
      body.style.whiteSpace = 'pre-wrap';
    }
    block.appendChild(body);

    if (message.role === 'agent' && message.trace?.length) {
      const trace = el('div', 'trace');
      const dataset = traceDataset(message.trace);
      trace.appendChild(el('b', undefined, 'How this was answered'));
      if (dataset) trace.appendChild(el('div', undefined, `Dataset: ${dataset}`));
      const list = el('ol');
      message.trace.forEach((step) => {
        const time = step.durationSeconds != null ? ` (${formatDuration(step.durationSeconds)})` : '';
        list.appendChild(el('li', undefined, `${doneLabel(step)}${time}${step.thought ? ` - ${step.thought}` : ''}`));
      });
      trace.appendChild(list);
      message.trace.forEach((step) =>
        step.dax.forEach((dax) => {
          const pre = el('pre');
          pre.appendChild(el('code', undefined, dax));
          trace.appendChild(pre);
        }),
      );
      block.appendChild(trace);
    }

    root.appendChild(block);
  });

  return root;
}

const PDF_TIMEOUT_MS = 30000;

// The renderer draws one CSS pixel as one PDF point, and an A4 page with 1.5cm margins has 510pt of width. The
// document is laid out 680px wide and scaled by 0.75 (510 / 680) so it fills the page exactly - at 1:1 it ran
// past the right edge and got clipped.
const PDF_SCALE = 0.75;

export async function exportChatAsPdf(chat: IExportChat): Promise<void> {
  const doc = buildPdfDocument(chat);
  // Must be attached and laid out for the PDF renderer to measure it, so it goes in a host kept at the top-left
  // but fully clipped, invisible to the user, and removed afterwards. The hiding styles sit on the host, not on
  // the document: drawDOM clones the document (inline styles included) into each page, and a cloned
  // position: absolute; left/top pins the content to the page corner, ignoring the page margins.
  const host = document.createElement('div');
  host.style.position = 'absolute';
  host.style.left = '0';
  host.style.top = '0';
  host.style.clipPath = 'inset(100%)';
  host.style.pointerEvents = 'none';
  host.setAttribute('aria-hidden', 'true');
  host.appendChild(doc);
  document.body.appendChild(host);

  // The renderer reports some failures (e.g. an unreadable font) by throwing inside its own callbacks, which
  // leaves its promise pending forever - so never wait on it without a deadline.
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('PDF export timed out')), PDF_TIMEOUT_MS);
  });

  try {
    const render = async () => {
      const group = await drawDOM(doc, {
        paperSize: 'A4',
        scale: PDF_SCALE,
        margin: { top: '1.5cm', right: '1.5cm', bottom: '1.5cm', left: '1.5cm' },
        keepTogether: '.msg',
      });
      return exportPDF(group);
    };
    const dataUri = await Promise.race([render(), deadline]);
    saveAs(dataUri, fileName(chat, 'pdf'));
  } finally {
    clearTimeout(timer);
    document.body.removeChild(host);
  }
}
